import dynamic from 'next/dynamic';
import Head from "next/head";
import {ScrollShadow} from "@nextui-org/react";
import SharedServiceInstance from "@/services/shared-service";
import SharedService from "@/services/shared-service";
import {
    getBanners,
    getCategoryData,
    getWidget,
    getGroup,
    getSliderBanner,
    getWidgets,
    getAudioCategory,
    getDeaneries,
} from "@/services/home-service";
import { NEWS_TYPE, HOMEPAGE_DATA_TYPE} from '@/components/helpers/constant.js';
import {GlobalSettings} from "@/configuration/global-settings";

import Link from "next/link";
import Image from "next/image";

var _ = require('lodash');

// Import components
const HeroCarousel = dynamic(() => import("@/components/shared/hero-carousel"));
const NewsListComponent = dynamic(() => import("@/components/news/news-list"));
const NotificationList = dynamic(() => import("@/components/notifications/notification-list"));
const HorizontalCardSlider = dynamic(() => import("@/components/shared/horizontal-card-slider"));
const AudioCarousel = dynamic(() => import("@/components/shared/audio-carousel"));
const ClergySearch = dynamic(() => import("@/components/shared/clergy-search"));
const Maps = dynamic(() => import("@/components/main-menu/maps"), {ssr: false});
const TopSliderCarousel = dynamic(() => import("@/components/shared/top-slider-carousel"));
const FooterNew = dynamic(() => import("@/components/footer/footer-new"));
const EventHeroSlider = dynamic(() => import("@/components/shared/event-hero-slider"));
const EventsCalendarCompact = dynamic(() => import("@/components/shared/events-calendar-compact"));
const VideoPlayerList = dynamic(() => import("@/components/shared/video-player-list"));
import DocumentsSlider from '@/components/main-menu/documents-slider';
import Taize from '@/components/shared/taize';

export async function getStaticProps() {
    // const deaneries = await getGroup({
    //     type: TYPE_SPECIAL_GROUP.DEANERY,
    //     sort: { attribute: 'name' }
    // });
    const deaneries = await getDeaneries();

    let result = {
        deaneries: deaneries || []
    }

    // Get Notifications - Parables - Audio
    let widgets = await getWidgets();
    result.sliderBanners = getSliderBanner(widgets);
    result.banners = getBanners(widgets);
    result.audio = await getAudioCategory();
    result.tikTok = await getWidget('tiktok-gppc');

    // Get News
    const newsObjKey = {
        'logos': NEWS_TYPE.LOGOS.link,
        'focusNew': NEWS_TYPE.FOCUS.link,
        'latestNews': NEWS_TYPE.LATEST.link,
        'mostView': NEWS_TYPE.MOST_VIEW.link,
        'dioceseNews': NEWS_TYPE.DIOCESE.code,
        'churchNews': NEWS_TYPE.CHURCH.code,
        'liturgyNews': NEWS_TYPE.LITURGY.code,
        'liturgyProblemsNews': NEWS_TYPE.LITURGY_PROBLEMS.code,
        'liturgyCalendarNews': NEWS_TYPE.LITURGY_CALENDAR.code,
        'logosCommonNews': NEWS_TYPE.LOGOS_COMMON.code,
        'liturgySaintsNews': NEWS_TYPE.LITURGY_SAINTS.code,
        'vnChurchNews': NEWS_TYPE.CHURCH_VN.code,
        'documents': NEWS_TYPE.MATERIAL_LIST.code,
        'universalNews': NEWS_TYPE.UNIVERSAL.code,
        'dossil': HOMEPAGE_DATA_TYPE.DOSSIL.link,
        'parables': HOMEPAGE_DATA_TYPE.PARABLES.link,
        'taizeNews': NEWS_TYPE.TAIZE.code,
        'saltForLifeNews': NEWS_TYPE.SALT_FOR_LIFE.code,
        'priestlyVocation': NEWS_TYPE.PRIESTLY_VOCATION.code,
        'notifications': HOMEPAGE_DATA_TYPE.NOTIFICATIONS.link,
    };

    const postTypes = ['logos', 'focusNew', 'vnChurchNews', 'universalNews', 'priestlyVocation'];
    const childEventName = ['dioceseNews', 'universalNews', 'vnChurchNews'];

    for (const [eventName, propertyName] of Object.entries(newsObjKey)) {
        result[eventName] = await getCategoryData(propertyName, childEventName.includes(eventName) ? {} : {status: 'latest'});
        if (postTypes.includes(eventName)) {
            result[eventName].value = _.filter(result[eventName].value, value => value?.photo);
            result[eventName].value = _.slice(result[eventName].value, 0, 6);
        } else if (eventName === 'latestNews') {
            result[eventName].value = _.slice(result[eventName].value, 0, 5);
        }
    }

    return {props: {...result}, revalidate: 10}
}

export default function HomeNew({
                                    logos,
                                    deaneries,
                                    dioceseNews,
                                    mostView,
                                    universalNews,
                                    latestNews,
                                    focusNew,
                                    churchNews,
                                    saltForLifeNews,
                                    vnChurchNews,
                                    notifications,
                                    parables,
                                    dossil,
                                    taizeNews,
                                    priestlyVocation,
                                    sliderBanners,
                                    banners,
                                    audio,
                                    liturgyNews,
                                    liturgyProblemsNews,
                                    liturgyCalendarNews,
                                    logosCommonNews,
                                    liturgySaintsNews
                                }) {
    // Transform data for new components
    const heroSlides = focusNew?.value?.slice(0, 5).map(item => ({
        id: item.id,
        image: SharedServiceInstance.isValidUrl(item.hotNewsPhoto)
            ? SharedServiceInstance.getOptimizeImage(item.hotNewsPhoto, 'top-slider')
            : SharedServiceInstance.getOptimizeImage(`${GlobalSettings.Settings.Server_New}/${item.hotNewsPhoto}`, 'top-slider'),
        badge: item.categories?.[0]?.name || item.categoryName || 'Tin tức',
        title: item.categories?.[0]?.name || item.categoryName || 'GIÁO PHẬN PHÚ CƯỜNG',
        heading: item.title,
        subtitle: item.metaDescription || item.description || '',
        link: SharedService.getPostPath(item)
    })) || [];

    const latestNewsData = latestNews?.value?.slice(0, 6).map(news => ({
        id: news.id,
        title: news.title || news.name,
        image: SharedServiceInstance.isValidUrl(news.photo)
            ? SharedServiceInstance.getOptimizeImage(news.photo, 'small-post-view')
            : news.photo ? `${GlobalSettings.Settings.Server_New}/${news.photo}` : '/webp/chanh-toa.webp',
        category: news.categoryName,
        description: news.metaDescription || news.description,
        date: SharedServiceInstance.formatISODate(news.created || news.publishedDate),
        link: SharedService.getPostPath(news)
    })) || [];

    const notificationsData = notifications?.value?.map(item => ({
        id: item.id,
        category: item.categories?.[0]?.name || 'Thông báo',
        title: item.title,
        date: SharedServiceInstance.formatISODate(item.created),
        link: SharedService.getPostPath(item)
    })) || [];

    const loiChuaData = logos?.value?.map(item => ({
        id: item.id,
        title: item.title,
        date: SharedServiceInstance.formatISODate(item.created),
        image: SharedServiceInstance.isValidUrl(item.photo)
            ? SharedServiceInstance.getOptimizeImage(item.photo, 'main-post-view')
            : item.photo ? `${GlobalSettings.Settings.Server_New}/${item.photo}` : '/webp/chanh-toa.webp',
        link: SharedService.getPostPath(item),
        description: item.metaDescription
    })) || [];

    const dioceseNewsData = dioceseNews?.value?.map(item => ({
        id: item.id,
        title: item.title,
        date: SharedServiceInstance.formatISODate(item.created),
        image: SharedServiceInstance.isValidUrl(item.photo)
            ? SharedServiceInstance.getOptimizeImage(item.photo, 'main-post-view')
            : item.photo ? `${GlobalSettings.Settings.Server_New}/${item.photo}` : '/webp/chanh-toa.webp',
        link: SharedService.getPostPath(item),
        description: item.metaDescription,
        excerpt: item.metaDescription  // Add excerpt for HorizontalCardSlider
    })) || [];

    const mapForHorizontal = (item) => ({
        id: item.id,
        title: item.title,
        date: SharedServiceInstance.formatISODate(item.created),
        image: SharedServiceInstance.isValidUrl(item.photo)
            ? SharedServiceInstance.getOptimizeImage(item.photo, 'main-post-view')
            : item.photo ? `${GlobalSettings.Settings.Server_New}/${item.photo}` : '/webp/chanh-toa.webp',
        link: SharedService.getPostPath(item),
        description: item.metaDescription,
        excerpt: item.metaDescription
    });

    const takeAndMap = (arr, count = 8) => (arr || []).slice(0, count).map(mapForHorizontal);
    const churchNewsCategories = {
        all: takeAndMap(churchNews?.value, 8),
        'tin-giao-hoi-viet-nam': takeAndMap(vnChurchNews?.value, 8),
        'tin-giao-hoi-hoan-vu': takeAndMap(universalNews?.value, 8)
    };
    const liturgyNewsCategories = {
        all: takeAndMap(liturgyNews?.value, 8),
        'cac-van-de-phung-vu': takeAndMap(liturgyProblemsNews?.value, 8),
        'lich-phung-vu': takeAndMap(liturgyCalendarNews?.value, 8),
        'loi-nguyen-chung': takeAndMap(logosCommonNews?.value, 8),
        'phung-vu-chu-thanh': takeAndMap(liturgySaintsNews?.value, 8),

    };

    const vocationData = priestlyVocation?.value?.map(item => ({
        id: item.id,
        title: item.title,
        date: SharedServiceInstance.formatISODate(item.created),
        image: SharedServiceInstance.isValidUrl(item.photo)
            ? SharedServiceInstance.getOptimizeImage(item.photo, 'main-post-view')
            : item.photo ? `${GlobalSettings.Settings.Server_New}/${item.photo}` : '/webp/chanh-toa.webp',
        link: SharedService.getPostPath(item),
        description: item.metaDescription
    })) || [];

    const mostViewData = mostView?.value?.map(item => ({
        id: item.id,
        title: item.title,
        image: SharedServiceInstance.isValidUrl(item.photo)
            ? SharedServiceInstance.getOptimizeImage(item.photo, 'small-post-view')
            : item.photo ? `${GlobalSettings.Settings.Server_New}/${item.photo}` : '/webp/chanh-toa.webp',
        category: item.categories?.[0]?.name || item.categoryName || '',
        description: item.metaDescription || '',
        date: SharedServiceInstance.formatISODate(item.created),
        link: SharedService.getPostPath(item)
    })) || [];

    const audioData = audio?.map(item => ({
        id: item.id,
        title: item.title || item.name,
        coverImage: SharedServiceInstance.isValidUrl(item.photo)
            ? SharedServiceInstance.getOptimizeImage(item.photo, 'small-post-view')
            : item.photo ? `${GlobalSettings.Settings.Server_New}/${item.photo}` : '/webp/chanh-toa.webp',
        audioUrl: item.mediaUrl || '',
        link: `/audio/${item.id}`
    })) || [];

    return (
        <div>
            <Head>
                <title>GIÁO PHẬN PHÚ CƯỜNG</title>
                <meta name="description" content="Chuyên trang truyền thông Giáo Phận Phú Cường"/>
                <meta name="keywords" content="Giáo Phận Phú Cường, giao phan phu cuong"/>
            </Head>

            {/* Hero Carousel + Latest News Side by Side */}
            <section className="md:grid md:grid-cols-12 md:items-stretch gap-4 mt-5">
                {/* Hero Carousel - Left Block */}
                <div className="md:col-span-9 h-[52vw] mb-5 md:h-full">
                    <div className="rounded-[5px] overflow-hidden shadow-md h-full">
                        <HeroCarousel slides={heroSlides} height="100%"/>
                    </div>
                </div>

                {/* Latest News - Right Block */}
                <div className="md:col-span-3 h-full">
                    <div className="bg-white rounded-[5px] h-full shadow-md flex flex-col">
                        <NewsListComponent
                            items={latestNewsData}
                            useScroll={true}
                            scrollHeight="450px"
                        />
                    </div>
                </div>
            </section>

            {/* Loi Chua - Full Width */}
            <section className="pt-5">
                <div className="bg-white rounded-[5px] p-5 shadow-md">
                    <div className="flex justify-between items-center mb-5">
                        <h2 className="text-primary font-bold text-2xl md:text-3xl">
                            {NEWS_TYPE.LOGOS.name.toUpperCase()}
                        </h2>
                        <a href={`/chuyen-muc/${NEWS_TYPE.LOGOS.code}`}
                           className="text-primary hover:underline text-sm whitespace-nowrap flex items-center gap-1">
                            <span>Xem tất cả</span>
                            <span>→</span>
                        </a>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {loiChuaData.slice(0, 4).map(item => (
                            <a key={item.id} href={item.link} className="group">
                                <div className="overflow-hidden rounded-lg shadow-sm">
                                    <Image
                                        src={item.image}
                                        alt={item.title}
                                        width={1000}
                                        height={1000}
                                        className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-300"
                                    />
                                </div>
                                <h3 className="font-bold text-sm mt-2.5 line-clamp-3 group-hover:text-primary transition-colors leading-snug">
                                    {item.title}
                                </h3>
                                <p className="text-xs text-gray-600 line-clamp-3 mt-1.5 leading-relaxed">{item.description}</p>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* TopSlider Carousel - Banner Slider with Dots */}
            <section className="pt-5">
                <TopSliderCarousel images={sliderBanners}/>
            </section>

            {/* Tin Giao Phan + Thong Bao */}
            <section className="md:grid md:grid-cols-12 md:items-stretch gap-4 pt-5">
                <div className="md:col-span-9 w-full h-full">
                    <div className="bg-white rounded-[5px] shadow-md h-full">
                        <div className="p-5 pb-0">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-primary font-bold text-2xl md:text-3xl">
                                    {NEWS_TYPE.DIOCESE.name.toUpperCase()}
                                </h2>
                                <a href={`/chuyen-de/${NEWS_TYPE.DIOCESE.code}`}
                                   className="text-primary hover:underline text-sm whitespace-nowrap flex items-center gap-1">
                                    <span>Xem tất cả</span>
                                    <span>→</span>
                                </a>
                            </div>
                        </div>
                        <div className="px-5">
                            <HorizontalCardSlider
                                items={dioceseNewsData}
                                showArrows={true}
                                showCategories={false}
                            />
                        </div>
                    </div>
                </div>
                <div className="md:col-span-3 w-full h-full">
                    <div className="h-full">
                        <NotificationList items={notificationsData} maxWidth="100%" showFooter={true} useScroll={true}/>
                    </div>

                </div>
            </section>

            {/* Tin Tuc Giao Hoi */}
            <section className="pt-5 grid grid-cols-12 gap-4">
                <div className="bg-white col-span-12 md:col-span-9 rounded-[5px] shadow-md">
                    <div className="p-5 pb-0">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-primary font-bold text-2xl md:text-3xl">
                                TIN TỨC GIÁO HỘI
                            </h2>
                            <a href="/chuyen-muc/tin-giao-hoi"
                               className="text-primary hover:underline text-sm whitespace-nowrap flex items-center gap-1">
                                <span>Xem tất cả</span>
                                <span>→</span>
                            </a>
                        </div>
                    </div>
                    <div className="px-5 pb-5">
                        <HorizontalCardSlider
                            itemsByCategory={churchNewsCategories}
                            showCategories={true}
                            listCate={[
                                {id: 'all', label: 'Tất Cả'},
                                {id: 'tin-giao-hoi-viet-nam', label: 'Tin Giáo Hội Việt Nam'},
                                {id: 'tin-giao-hoi-hoan-vu', label: 'Tin Giáo Hội Hoàn Vũ'}
                            ]}
                        />
                    </div>
                </div>

                <div className="col-span-12 md:col-span-3 rounded-[5px] shadow-md">
                    <iframe
                        src="https://thongtinlinhmuc.cmate.vn/"
                        title="Thông Tin Linh Mục"
                        width="100%"
                        height="500px"
                    >
                    </iframe>
                </div>
            </section>

            {/* Van Kien */}
            <section className="pt-5">
                <DocumentsSlider data={dossil} type="DOSSIL"/>
            </section>

            {/* Phụng Vụ */}
            <section className="pt-5">
                <div className="bg-white rounded-[5px] shadow-md">
                    <div className="p-5 pb-0">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-primary font-bold text-2xl md:text-3xl">
                                PHỤNG VỤ
                            </h2>
                            <a href="/chuyen-muc/phung-vu"
                               className="text-primary hover:underline text-sm whitespace-nowrap flex items-center gap-1">
                                <span>Xem tất cả</span>
                                <span>→</span>
                            </a>
                        </div>
                    </div>
                    <div className="px-5 pb-5">
                        {/* TODO: */}
                        <HorizontalCardSlider
                            // No sub-cate
                            items={liturgyNewsCategories.all}
                            showCategories={false}
                            // Having sub-cates
                            // showCategories={true}
                            // itemsByCategory={liturgyNewsCategories}
                            listCate={[
                                {id: 'all', label: 'Tất Cả'},
                                {id: 'cac-van-de-phung-vu', label: 'Các Vấn Đề Phụng Vụ'},
                                {id: 'lich-phung-vu', label: 'Lịch Phụng Vụ'},
                                {id: 'loi-nguyen-chung', label: 'Lời Nguyện Chung'},
                                {id: 'phung-vu-chu-thanh', label: 'Phụng Vụ Chư Thánh'}
                            ]}
                        />
                    </div>
                </div>
            </section>

            {taizeNews?.value.length > 0 && (
                <Taize data={taizeNews?.value} />
            )}

            {/* On Goi Linh Muc + Bai Xem Nhieu */}
            <section className="md:grid md:grid-cols-12 md:items-stretch gap-4 pt-5">
                <div className="md:col-span-9 w-full h-full">
                    <div className="bg-white rounded-[5px] p-5 pb-0 shadow-md h-full flex flex-col mb-5 md:mb-0">
                        <div className="flex justify-between items-center mb-5">
                            <h2 className="text-primary font-bold text-2xl md:text-3xl">
                                {NEWS_TYPE.PRIESTLY_VOCATION.name.toUpperCase()}
                            </h2>
                            <a href={`/chuyen-de/${NEWS_TYPE.PRIESTLY_VOCATION.code}`}
                               className="text-primary hover:underline text-sm whitespace-nowrap flex items-center gap-1">
                                <span>Xem tất cả</span>
                                <span>→</span>
                            </a>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1">
                            {vocationData.slice(0, 3).map(item => (
                                <Link key={item.id} href={item.link} className="group">
                                    <div className="overflow-hidden rounded">
                                        <Image
                                            width={1000}
                                            height={1000}
                                            src={item.image}
                                            alt={item.title}
                                            className="w-full aspect-video object-cover group-hover:scale-105 transition-transform"
                                        />
                                    </div>
                                    <h3 className="font-bold text-base mt-2.5 line-clamp-3 group-hover:text-primary leading-snug h-[66px]">
                                        {item.title}
                                    </h3>
                                    <p className="text-xs text-gray-600 line-clamp-3 mt-1.5 leading-relaxed">{item.description}</p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="md:col-span-3 w-full h-full">
                    <div className="bg-white rounded-[5px] shadow-md h-full overflow-hidden">
                        <div className="bg-primary text-white p-4 text-center">
                            <h2 className="font-bold text-lg">
                                {NEWS_TYPE.MOST_VIEW.name.toUpperCase()}
                            </h2>
                        </div>
                        <ScrollShadow hideScrollBar className="h-[400px]">
                            <div className="divide-y divide-gray-200">
                                {mostViewData.map((item, index) => (
                                    <a
                                        key={item.id}
                                        href={item.link}
                                        className="flex items-start gap-3 p-3 hover:bg-gray-50 transition-colors"
                                    >
                                        <img
                                            src={item.image}
                                            alt={item.title}
                                            className="w-24 h-16 object-cover rounded flex-shrink-0"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-medium text-sm line-clamp-2 text-gray-900 mb-1">
                                                {item.title}
                                            </h3>
                                            <p className="text-xs text-gray-500">
                                                {item.date}
                                            </p>
                                        </div>
                                    </a>
                                ))}
                            </div>
                        </ScrollShadow>
                    </div>
                </div>
            </section>

            {/* Event Hero Slider + Events Calendar */}
            <section className="md:grid md:grid-cols-12 md:items-stretch gap-4 pt-5">
                <div className="md:col-span-9 w-full h-full">
                    <EventHeroSlider events={parables?.value || []}/>
                </div>
                <div className="md:col-span-3 w-full h-full">
                    <EventsCalendarCompact/>
                </div>
            </section>

            {/* Muoi Men Cho Doi */}
            <section className="pt-5">
                <VideoPlayerList
                    data={saltForLifeNews}
                    category={{
                        categoryCode: 'muoi-men-cho-doi',
                        name: 'Muối Men Cho Đời',
                        link: '/chuyen-muc/muoi-men-cho-doi',
                    }}
                />
            </section>

            {/* Audio */}
            <section className="pt-5">
                <div className="bg-white rounded-[5px] p-5 shadow-md">
                    <div className="flex justify-between items-center mb-5">
                        <h2 className="text-primary font-bold text-2xl md:text-3xl">AUDIO</h2>
                        <Link href="/audio"
                              className="text-primary hover:underline text-sm whitespace-nowrap flex items-center gap-1">
                            <span>Xem tất cả</span>
                            <span>→</span>
                        </Link>
                    </div>
                    <AudioCarousel items={audioData} title=""/>
                </div>
            </section>

            {/* Giao Phan Search + Map */}
            <section className="py-5">
                <div className="bg-white rounded-[5px] p-5 shadow-md">
                    <ClergySearch deaneries={deaneries}/>
                    <div className="mt-8">
                        <Maps sourceMap="https://maps.giaophanphucuong.org?mode=iframe&size=xl"/>
                    </div>
                </div>
            </section>

            {/* <FooterNew sharedService={SharedServiceInstance} /> */}
        </div>
    );
}
